import { useState } from 'react';

interface FormData {
  businessName: string;
  contactName: string;
  email: string;
  phone: string;
  website: string;
  contentType: string;
  preferredDate: string;
  budget: string;
  contentIdeas: string;
  targetAudience: string;
  goals: string;
}

export default function SponsoredContentForm() {
  const [formData, setFormData] = useState<FormData>({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    contentType: '',
    preferredDate: '',
    budget: '',
    contentIdeas: '',
    targetAudience: '',
    goals: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6hh2kcbcbcsu13rg780', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          businessName: '',
          contactName: '',
          email: '',
          phone: '',
          website: '',
          contentType: '',
          preferredDate: '',
          budget: '',
          contentIdeas: '',
          targetAudience: '',
          goals: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full mb-4">
            <i className="ri-article-line text-teal-600"></i>
            <span className="text-teal-700 text-sm font-semibold">Sponsored Content</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Submit Your Sponsored Content Request
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Reach thousands of Roseville residents with native advertising that tells your story
          </p>
        </div>

        <form 
          id="sponsored-content-form"
          data-readdy-form
          onSubmit={handleSubmit} 
          className="bg-gray-50 rounded-2xl p-8 border border-gray-200"
        >
          {/* Business Information */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-building-line text-teal-600"></i>
              Business Information
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="businessName" className="block text-sm font-semibold text-gray-700 mb-2">
                  Business Name *
                </label>
                <input
                  type="text"
                  id="businessName"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Your Business Name"
                />
              </div>
              <div>
                <label htmlFor="contactName" className="block text-sm font-semibold text-gray-700 mb-2">
                  Contact Name *
                </label>
                <input
                  type="text"
                  id="contactName"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Your Full Name"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="you@business.com"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="(916) 555-0123"
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="website" className="block text-sm font-semibold text-gray-700 mb-2">
                  Website URL
                </label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="https://yourbusiness.com"
                />
              </div>
            </div>
          </div>

          {/* Content Details */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-file-text-line text-teal-600"></i>
              Content Details
            </h3>
            <div className="space-y-6">
              <div>
                <label htmlFor="contentType" className="block text-sm font-semibold text-gray-700 mb-2">
                  Content Type *
                </label>
                <select
                  id="contentType"
                  name="contentType"
                  value={formData.contentType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="">Select content type...</option>
                  <option value="blog-post">Sponsored Blog Post ($250-$500)</option>
                  <option value="newsletter">Newsletter Sponsor ($150-$300)</option>
                  <option value="homepage-spotlight">Homepage Business Spotlight ($200-$400/week)</option>
                  <option value="multiple">Multiple Placements (Custom Package)</option>
                </select>
              </div>

              <div>
                <label htmlFor="preferredDate" className="block text-sm font-semibold text-gray-700 mb-2">
                  Preferred Publication Date
                </label>
                <input
                  type="date"
                  id="preferredDate"
                  name="preferredDate"
                  value={formData.preferredDate}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="budget" className="block text-sm font-semibold text-gray-700 mb-2">
                  Budget Range *
                </label>
                <select
                  id="budget"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="">Select budget range...</option>
                  <option value="150-300">$150 - $300</option>
                  <option value="300-500">$300 - $500</option>
                  <option value="500-1000">$500 - $1,000</option>
                  <option value="1000+">$1,000+</option>
                </select>
              </div>

              <div>
                <label htmlFor="contentIdeas" className="block text-sm font-semibold text-gray-700 mb-2">
                  Content Ideas & Topics *
                </label>
                <textarea
                  id="contentIdeas"
                  name="contentIdeas"
                  value={formData.contentIdeas}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  placeholder="Describe your story ideas, key messages, or what you'd like to feature..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.contentIdeas.length}/500 characters
                </p>
              </div>

              <div>
                <label htmlFor="targetAudience" className="block text-sm font-semibold text-gray-700 mb-2">
                  Target Audience
                </label>
                <input
                  type="text"
                  id="targetAudience"
                  name="targetAudience"
                  value={formData.targetAudience}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="e.g., Young families, homeowners, fitness enthusiasts"
                />
              </div>

              <div>
                <label htmlFor="goals" className="block text-sm font-semibold text-gray-700 mb-2">
                  Campaign Goals *
                </label>
                <textarea
                  id="goals"
                  name="goals"
                  value={formData.goals}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={3}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  placeholder="What do you hope to achieve? (e.g., brand awareness, drive traffic, promote new service)"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.goals.length}/500 characters
                </p>
              </div>
            </div>
          </div>

          {/* Submit Status Messages */}
          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <i className="ri-checkbox-circle-fill text-green-600 text-xl"></i>
              <div>
                <p className="text-green-800 font-semibold">Request Submitted Successfully!</p>
                <p className="text-green-700 text-sm mt-1">
                  We'll review your sponsored content request and contact you within 1-2 business days.
                </p>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <i className="ri-error-warning-fill text-red-600 text-xl"></i>
              <div>
                <p className="text-red-800 font-semibold">Submission Failed</p>
                <p className="text-red-700 text-sm mt-1">
                  Please try again or contact us directly at advertise@roseville.org
                </p>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-8 py-4 bg-teal-600 text-white rounded-lg font-bold text-lg hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
          >
            {isSubmitting ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Submitting...
              </>
            ) : (
              <>
                <i className="ri-send-plane-fill"></i>
                Submit Request
              </>
            )}
          </button>

          <p className="text-xs text-gray-500 text-center mt-4">
            By submitting this form, you agree to our advertising terms and conditions.
          </p>
        </form>
      </div>
    </section>
  );
}